export type FilterJoin = "or" | "and"

export interface CombineOps<R> {
  intersect: (left: R, right: R) => R
  union: (left: R, right: R) => R
  empty: R
}

/**
 * Top-to-bottom left-associative reduce of `entries` using per-pair `joins`.
 *
 * `joins[i]` connects the accumulator with `entries[i + 1]`. Missing entries
 * default to "or". Used by both audio and transcript previews via different
 * `R` types (Range[] vs Set<number>); shared so the FE/BE parity contract
 * lives in one place.
 */
export function combineWithJoins<E, R>(
  entries: E[],
  joins: FilterJoin[],
  resolve: (entry: E) => R,
  ops: CombineOps<R>,
): R {
  if (entries.length === 0) {
    return ops.empty
  }
  let result = resolve(entries[0])
  for (let i = 1; i < entries.length; i++) {
    const next = resolve(entries[i])
    const operator = joins[i - 1] ?? "or"
    result =
      operator === "and" ? ops.intersect(result, next) : ops.union(result, next)
  }
  return result
}
